import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select as GrommetSelect } from 'grommet';
import { observer } from 'mobx-react-lite';
import { Box, BoxProps, Text, TextProps } from '/src/components';
import { useSearchStore, useUserStore } from '/src/context';

export const SearchEntitySelect: React.FC<SearchEntitySelectProps> = observer((props) => {
  const { labelSize, labelWeight, ...boxProps } = props;

  const { t } = useTranslation();
  const searchStore = useSearchStore();
  const userStore = useUserStore();

  const entityOptions = useMemo(() => {
    const entities: string[] = [t('navigation:facilities'), t('navigation:equipment')];

    if (userStore.isInternalUser) entities.unshift(t('navigation:clients'));
    if (!userStore.isFacilityAdmin) entities.push(t('navigation:users'));

    return entities.filter((entity) => entity.toLowerCase().includes(searchStore.entityFilter.toLowerCase()));
  }, [searchStore.entityFilter, userStore.isInternalUser, userStore.isFacilityAdmin, t]);

  return (
    <Box direction="row" fill="horizontal" {...boxProps}>
      <Box align="center" justify="center" width="20%">
        <Text toUpperCase size={labelSize || 'small'} weight={labelWeight || 300}>
          {t('common:find')}
        </Text>
      </Box>
      <Box
        justify="center"
        fontFamily="Lato, sans-serif"
        width="80%"
        border={{ color: searchStore.isEntityFocused ? 'accent-1' : 'light-5' }}
        round="6px"
      >
        <GrommetSelect
          plain
          name="search_entity"
          dropAlign={{ top: 'bottom', right: 'right' }}
          value={searchStore.searchEntity}
          placeholder={t('common:choose')}
          focusIndicator
          options={entityOptions}
          searchPlaceholder={t('common:findFieldByName')}
          onFocus={() => searchStore.setIsEntityFocused(true)}
          onBlur={() => searchStore.setIsEntityFocused(false)}
          onClose={() => searchStore.setEntityFilter('')}
          onSearch={(filter) => searchStore.setEntityFilter(filter)}
          onChange={({ value }) => searchStore.setSearchEntity(value)}
        />
      </Box>
    </Box>
  );
});

export type SearchEntitySelectProps = BoxProps & {
  labelSize?: TextProps['size'];
  labelWeight?: TextProps['weight'];
};
